import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import {
  CONTACT_INQUIRY_CATEGORY_LABELS,
  CONTACT_INQUIRY_STATUSES,
  CONTACT_INQUIRY_STATUS_LABELS,
  type ContactInquiryStatus,
} from '@kulum/shared';
import { fetchContactInquiries, type AdminContactInquiry } from '@/lib/inquiryService';
import {
  Badge,
  Button,
  DataTable,
  Pagination,
  ResizableDetailLayout,
  type Column,
  Loading,
} from '@/components/ui';
import { InquiryDetailPanel } from './InquiryDetailPage';

type StatusFilter = ContactInquiryStatus | 'all';
const PAGE_SIZE = 20;

function formatDateTime(date?: Date): string {
  if (!date) {
    return '-';
  }

  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function getStatusBadge(status: ContactInquiryStatus) {
  const variantMap: Record<ContactInquiryStatus, 'default' | 'warning' | 'info' | 'success'> = {
    open: 'warning',
    in_progress: 'info',
    resolved: 'success',
  };

  return <Badge variant={variantMap[status]}>{CONTACT_INQUIRY_STATUS_LABELS[status]}</Badge>;
}

export function InquiriesPage() {
  const [inquiries, setInquiries] = useState<AdminContactInquiry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedInquiryId, setSelectedInquiryId] = useState<string | null>(null);

  const loadInquiries = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const nextInquiries = await fetchContactInquiries();
      setInquiries(nextInquiries);
    } catch (loadError) {
      console.error('Failed to fetch contact inquiries:', loadError);
      setError('問い合わせ一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInquiries();
  }, [loadInquiries]);

  const filteredInquiries = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return inquiries.filter((inquiry) => {
      if (statusFilter !== 'all' && inquiry.status !== statusFilter) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const haystacks = [
        inquiry.userDisplayName,
        inquiry.userEmail,
        inquiry.message,
        CONTACT_INQUIRY_CATEGORY_LABELS[inquiry.category],
      ];

      return haystacks.some((value) => value.toLowerCase().includes(normalizedQuery));
    });
  }, [inquiries, searchQuery, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredInquiries.length / PAGE_SIZE));
  const pagedInquiries = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredInquiries.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentPage, filteredInquiries]);
  const visibleStart =
    filteredInquiries.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const visibleEnd = Math.min(currentPage * PAGE_SIZE, filteredInquiries.length);

  const handleSearchQueryChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const columns: Column<AdminContactInquiry>[] = [
    {
      key: 'createdAt',
      header: '受付日時',
      render: (inquiry) => <span className="text-xs text-admin-muted">{formatDateTime(inquiry.createdAt)}</span>,
    },
    {
      key: 'user',
      header: 'ユーザー',
      render: (inquiry) => (
        <div>
          <p className="font-semibold text-admin-ink">{inquiry.userDisplayName || '不明'}</p>
          <p className="text-xs text-admin-muted">{inquiry.userEmail || '-'}</p>
          <p className="text-xs text-admin-muted">UID: {inquiry.userId || '-'}</p>
        </div>
      ),
    },
    {
      key: 'category',
      header: '種別',
      render: (inquiry) => CONTACT_INQUIRY_CATEGORY_LABELS[inquiry.category],
    },
    {
      key: 'status',
      header: '状態',
      render: (inquiry) => getStatusBadge(inquiry.status),
    },
    {
      key: 'message',
      header: '内容',
      render: (inquiry) => (
        <p className="line-clamp-2 min-w-[280px] max-w-[420px] text-sm text-admin-ink">{inquiry.message}</p>
      ),
    },
  ];

  if (isLoading) {
    return <Loading fullScreen message="問い合わせ一覧を読み込み中..." />;
  }

  return (
    <ResizableDetailLayout
      isOpen={Boolean(selectedInquiryId)}
      detail={
        selectedInquiryId ? (
          <InquiryDetailPanel
            inquiryId={selectedInquiryId}
            embedded
            onClose={() => setSelectedInquiryId(null)}
            onUpdated={() => void loadInquiries()}
          />
        ) : null
      }
      list={
        <section className="space-y-5 p-5 md:p-8">
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-2xl font-bold text-admin-ink">問い合わせ管理</h2>
              <p className="mt-1 text-sm text-admin-muted">
                アプリ内の問い合わせを一覧で確認し、対応ステータスを管理します。
              </p>
            </div>
            <Button
              type="button"
              onClick={() => void loadInquiries()}
              disabled={isLoading}
              variant="secondary"
            >
              再読み込み
            </Button>
          </header>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="rounded-2xl border border-admin-border bg-white p-4 shadow-sm">
            <div className="grid gap-3 md:grid-cols-[1fr_200px]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-admin-muted" />
                <input
                  type="search"
                  value={searchQuery}
                  placeholder="表示名・メール・内容で検索"
                  onChange={(event) => handleSearchQueryChange(event.target.value)}
                  className="w-full rounded-xl border border-admin-border py-2 pl-9 pr-3 text-sm focus:border-admin-accent focus:outline-none focus:ring-2 focus:ring-admin-accent/20"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(event) => handleStatusFilterChange(event.target.value as StatusFilter)}
                className="rounded-xl border border-admin-border px-3 py-2 text-sm focus:border-admin-accent focus:outline-none focus:ring-2 focus:ring-admin-accent/20"
              >
                <option value="all">すべての状態</option>
                {CONTACT_INQUIRY_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {CONTACT_INQUIRY_STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <DataTable
            data={pagedInquiries}
            columns={columns}
            onRowClick={(inquiry) => setSelectedInquiryId(inquiry.id)}
            emptyMessage="問い合わせはまだありません"
          />

          {!error && (
            <div className="text-xs text-admin-muted">
              表示件数: {visibleStart}-{visibleEnd} / {filteredInquiries.length}
              {filteredInquiries.length !== inquiries.length ? `（全${inquiries.length}件）` : ''}
            </div>
          )}

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </section>
      }
    />
  );
}
